import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	compareBooleansDesc,
	compareObjects,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Rule,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { EmptyView } from "../../../../../Components/Empty";
import { Icon, IconContainer } from "../../../../../Components/Icon";
import { SearchField } from "../../../../../Components/Search";
import { User } from "../../../../../Utilities";
import { UserFilterFormState, UserFilterFormUtils } from "../../../../Filter";
import { UserRecipientList, UserRecipientSection } from "../Recipients";
import { MessagingUserCategory, userCategories } from "./user-categories";
import { useTeacherRecipients } from "./utils";
import styles from "./recipients.module.css";

export type MessageRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	onClickUserCategory: (id: MessagingUserCategory["id"]) => void;
	selectedUserIDs?: User["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function MessageRecipientSections({
	onClickUserRecipient,
	onClickUserCategory,
	selectedUserIDs,
	className,
	style,
}: MessageRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const filterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	const { data: usersData } = useTeacherRecipients(filterInput.search, filterInput);
	const teachers = React.useMemo(() => {
		if (!usersData) {
			return [];
		}
		return usersData.userConnection.edges.map((e) => e.node);
	}, [usersData]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			<If condition={teachers.length > 0}>
				<UserRecipientSection
					key="Teachers"
					name="Your Teachers"
					users={teachers}
					onClickRecipient={onClickUserRecipient}
					selectedUserIDs={selectedUserIDs}
				/>

				<Rule thickness="2px" />
			</If>

			<Column justify="spaced-start" className={className} style={style}>
				<Heading.H4 noMargin>Questions About...</Heading.H4>
				<Grid tracks="auto-fill" minTrackWidth="10rem" gap="0.75rem">
					{userCategories.map((userCategory) => (
						<UserCategoryButton key={userCategory.id} userCategory={userCategory} onClick={onClickUserCategory} />
					))}
				</Grid>
			</Column>
		</Column>
	);
}

type UserCategoryButtonProps = {
	userCategory: MessagingUserCategory;
	onClick: (id: MessagingUserCategory["id"]) => void;
};

function UserCategoryButton({ userCategory, onClick }: UserCategoryButtonProps) {
	const fullOnClick = React.useCallback(() => {
		onClick(userCategory.id);
	}, [onClick, userCategory.id]);

	return (
		<Button onClick={fullOnClick} className={styles["user-category-button"]}>
			<Column justify="spaced-start" align="center">
				<IconContainer
					color={userCategory.iconBackgroundColor}
					className={styles["user-category-button__icon-container"]}
				>
					<userCategory.Icon
						size={56}
						color={userCategory.iconColor}
						className={styles["user-category-button__icon"]}
					/>
				</IconContainer>

				<TruncatedText className={styles["user-category-button__name"]}>{userCategory.name}</TruncatedText>
			</Column>
		</Button>
	);
}

const defaultSelectedUserIDs: User["id"][] = [];

export type MessageEditRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function MessageEditRecipientSections({
	onClickUserRecipient,
	selectedUserIDs = defaultSelectedUserIDs,
	className,
	style,
}: MessageEditRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const filterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	const { loading: loadingRecipients, data: usersData } = useTeacherRecipients(filterInput.search, filterInput);

	const isLoading = loadingRecipients;
	const [initialSelectedUserIDs] = React.useState(selectedUserIDs);
	const users = React.useMemo(() => {
		return (
			usersData?.userConnection.edges
				.map((e) => e.node)
				.sort(compareObjects((e) => initialSelectedUserIDs.includes(e.id), compareBooleansDesc())) ?? []
		);
	}, [usersData, initialSelectedUserIDs]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			<Conditional>
				<If condition={users.length > 0}>
					<UserRecipientList
						users={users}
						selectedUserIDs={selectedUserIDs}
						onClickUserRecipient={onClickUserRecipient}
						paginationSyncID={userFilterFormState.formValues.search}
					/>
				</If>
				<If condition={isLoading}>Loading...</If>
				<Else>
					<EmptyView
						IconComponent={Icon.Users}
						iconSize={48}
						primaryText="Recipients"
						secondaryText="No relevant recipients to show."
					/>
				</Else>
			</Conditional>
		</Column>
	);
}
